import React from 'react';
import { Card } from '../ui/card';
import { 
  Ticket, 
  ShoppingCart, 
  TrendingUp, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  AlertTriangle,
  Link2,
  Shuffle
} from 'lucide-react';
import { cn } from '../../lib/utils';

const KPICard = ({ icon: Icon, label, value, subValue, valueAmount, colorClass, testId, onClick }) => (
  <div 
    data-testid={testId}
    onClick={onClick}
    className={cn(
      "kpi-card relative overflow-hidden group",
      onClick ? "cursor-pointer hover:scale-105 transition-transform" : "cursor-default"
    )}
  >
    <div className="flex items-start justify-between relative z-10">
      <div>
        <p className="text-xs uppercase tracking-wide text-white font-semibold mb-1">
          {label}
        </p>
        <p className="text-2xl font-heading font-bold text-white">
          {value}
        </p>
        {subValue && (
          <p className="text-xs text-[#C4A052]/80 mt-1 font-mono">
            {subValue}
          </p>
        )}
        {valueAmount > 0 && (
          <p className="text-xs text-emerald-400 mt-1 font-mono">
            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(valueAmount)}
          </p>
        )}
      </div>
      <div className={cn('p-2 rounded-lg', colorClass)}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
    {/* Gold line at bottom */}
    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#C4A052]/50 to-transparent" />
  </div>
);

export const StatsCards = ({ stats, onTabChange, onFilter }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  // Format: "lignes (billets)" - only show parentheses if different
  const formatCount = (lines, qty) => {
    if (qty && qty !== lines) {
      return `${lines} (${qty})`;
    }
    return lines;
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4">
      <KPICard
        testId="kpi-total"
        icon={Ticket}
        label="Total"
        value={formatCount(stats?.total_tickets || 0, stats?.total_qty)}
        colorClass="bg-[#C4A052]/15 text-[#C4A052]"
        onClick={() => { onTabChange?.('tout'); onFilter?.(null); }}
      />
      <KPICard
        testId="kpi-achats"
        icon={ShoppingCart}
        label="Achats"
        value={formatCount(stats?.total_achats || 0, stats?.achat_qty)}
        subValue={formatCurrency(stats?.total_achat_value)}
        colorClass="bg-emerald-400/15 text-emerald-400"
        onClick={() => { onTabChange?.('achat'); onFilter?.(null); }}
      />
      <KPICard
        testId="kpi-ventes"
        icon={TrendingUp}
        label="Ventes"
        value={formatCount(stats?.total_ventes || 0, stats?.vente_qty)}
        subValue={formatCurrency(stats?.total_vente_value)}
        colorClass="bg-red-400/15 text-red-400"
        onClick={() => { onTabChange?.('vente'); onFilter?.(null); }}
      />
      <KPICard
        testId="kpi-ventes-attribuees"
        icon={CheckCircle2}
        label="Ventes Attr."
        value={formatCount(stats?.ventes_attribuees || stats?.matched || 0, stats?.ventes_attribuees_qty || stats?.attribue_qty)}
        subValue="Ventes liées"
        colorClass="bg-blue-400/15 text-blue-400"
        onClick={() => { onTabChange?.('vente'); onFilter?.('attribue'); }}
      />
      <KPICard
        testId="kpi-achats-attribues"
        icon={Link2}
        label="Achats Attr."
        value={formatCount(stats?.achats_attribues || 0, stats?.achats_attribues_qty)}
        subValue="Achats liés"
        colorClass="bg-purple-400/15 text-purple-400"
        onClick={() => { onTabChange?.('achat'); onFilter?.('attribue'); }}
      />
      {/* MATCH OK card removed - not needed */}
      <KPICard
        testId="kpi-special"
        icon={Shuffle}
        label="Attr. Spéciale"
        value={stats?.special || 0}
        subValue={`V: ${stats?.special_vente || 0} | A: ${stats?.special_achat || 0}`}
        colorClass="bg-amber-400/15 text-amber-400"
        onClick={() => { onTabChange?.('tout'); onFilter?.('SPECIAL_MATCH'); }}
      />
      <KPICard
        testId="kpi-pending"
        icon={Clock}
        label="En attente"
        value={stats?.pending || 0}
        colorClass="bg-zinc-400/15 text-zinc-400"
        onClick={() => { onTabChange?.('tout'); onFilter?.('PENDING'); }}
      />
      <KPICard
        testId="kpi-validated"
        icon={CheckCircle2}
        label="Validés"
        value={stats?.validated || 0}
        subValue={`V: ${stats?.validated_vente || 0} | A: ${stats?.validated_achat || 0}`}
        valueAmount={stats?.validated_value || 0}
        colorClass="bg-emerald-400/15 text-emerald-400"
        onClick={() => { onTabChange?.('tout'); onFilter?.('VALIDE'); }}
      />
      <KPICard
        testId="kpi-cancelled"
        icon={XCircle}
        label="Annulés"
        value={stats?.cancelled || 0}
        subValue={`V: ${stats?.cancelled_vente || 0} | A: ${stats?.cancelled_achat || 0}`}
        colorClass="bg-red-400/15 text-red-400"
        onClick={() => { onTabChange?.('tout'); onFilter?.('ANNULEE'); }}
      />
    </div>
  );
};

export default StatsCards;
